import {EventEmitter, Injectable} from '@angular/core';
import {JobStatus} from '../../data/jobStatus';
import {JobDto} from '../../data/jobDto';
import {AppService} from '../../../app-common/services/app/app.service';
import {RequestsWebsocketService} from '../../../app-common/services/server/requests-websocket.service';
import {AddJobRequest} from '../../data/requests/addJobRequest';
import {EditJobRequest} from '../../data/requests/editJobRequest';
import {LexoRank} from 'lexorank';
import {ChangeLexoRankRequest} from '../../data/requests/changeLexoRankRequest';
import {ChangeFlaggedStatusRequest} from '../../data/requests/changeFlaggedStatusRequest';
import {PleaseWaitService} from '../../../app-common/services/please-wait/please-wait.service';
import {EmailNotificationDto} from '../../data/emailNotificationDto';
import {FlagColor} from '../../../flag/data/flagColor';
import {Query} from '../../../query/data/query';
import {QueryableProperty} from '../../../query/data/queryableProperty';
import {QueryCreator, QueryDTO, QueryFilter, QuerySort, QueryUnion} from '../../../query/data/queryCreator';
import {JobProperties} from '../../../query/data/specifics/jobProperties';
import {EqualsValuePropertyFilter} from '../../../query/data/equalsValuePropertyFilter';
import {Job} from '../../data/job';
import {ApiService} from '../../../app-common/services/server/api.service';
import {ConflictException} from '../../../app-common/services/server/conflictException';
import {UserService} from '../../../user/services/user/user.service';
import {FlagService} from '../../../flag/services/flag.service';
import {LoggerService} from '../../../logging/services/logger.service';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';
import {GraphJobDto} from '../../data/graphJobDto';
import {GenericVersion} from '../../../audit/data/GenericVersion';

@Injectable({
  providedIn: 'root'
})
export class JobService {

  onConflict: EventEmitter<{ my: JobDto, db: JobDto }> = new EventEmitter<{ my: JobDto; db: JobDto }>();
  openJobListPopupForCk: EventEmitter<EventEmitter<string>> = new EventEmitter<EventEmitter<string>>();

  constructor(public websocket: RequestsWebsocketService,
              public appService: AppService,
              public httpService: ApiService,
              public pleaseWaitService: PleaseWaitService,
              private flagService: FlagService,
              public userService: UserService,
              private loggerService: LoggerService) {
    this.websocket = websocket;
    this.appService = appService;
    this.httpService = httpService;
    this.userService = userService;
  }

  createEqualsFilter(property: QueryableProperty, value: any): EqualsValuePropertyFilter {
    const result = new EqualsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = value;
    return result;
  }

  buildGetJobsForCustomerQuery(customerId: number, entriesPerPage: number, pageNumber: number): QueryDTO {
    const jobProperties = new JobProperties(this.flagService);
    const rootQuery = new Query();

    for (const property of jobProperties.properties) {
      if (property.name === 'customerId') {
        rootQuery.filters.push(this.createEqualsFilter(property, customerId));
      }
    }
    const sorting = new QuerySort();
    sorting.dir = jobProperties.sortingDescending ? 'desc' : 'asc';
    sorting.field = jobProperties.sortingPropertyName;

    return QueryCreator.generateFilter(rootQuery, sorting, entriesPerPage, pageNumber, 'and');
  }

  async getJob(jobId: number, force: boolean = false): Promise<JobDto> {
    let response = await this.httpService.applicationApiClient.getWithParams<JobDto>('Job/' + jobId, {force});
    if (response.job.dueDate) {
      response.job.dueDate = new Date(response.job.dueDate);
    }
    response.job.creationDate = new Date(response.job.creationDate);
    response = this.trimJobDtoNames(response);
    return response;
  }

  async getChildren(jobId: number): Promise<JobDto[]> {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
      message: 'Get children for job', entity: jobId
    });
    const jobsDto = await this.httpService.applicationApiClient.get<JobDto[]>('Job/Children/' + jobId);
    if (jobsDto) {
      jobsDto.forEach(job => {
        job = this.trimJobDtoNames(job);
      });
      return jobsDto;
    }
  }

  async getAllJobsByCustomerId(customerId: number, query: string): Promise<JobDto[]> {
    if (customerId <= 0 || customerId === undefined) {
      this.appService.showErrorMessage('Please set a contact.');
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, 'Get all jobs, no contact');
      return;
    }
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
      message: 'Get all jobs by contact', entity: customerId
    });
    return await this.httpService.applicationApiClient.get<JobDto[]>(`Job/Customer/${customerId}?query=${query}`);
  }

  async getGraphJobsByCustomerId(customerId: number): Promise<GraphJobDto[]> {
    if (customerId <= 0 || customerId === undefined) {
      this.appService.showErrorMessage('Please set a contact.');
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, 'Get all jobs for GRAPH, no contact');
      return;
    }
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
      message: 'Get all jobs by contact, for GRAPH', entity: customerId
    });
    return await this.httpService.applicationApiClient.get<GraphJobDto[]>(`Job/Customer/${customerId}/Graph`);
  }

  getDummyJob(): Job {
    const dummy = new Job();
    dummy.id = 1;
    dummy.parentId = null;
    dummy.title = '';
    dummy.description = '';
    dummy.status = JobStatus.ToDo;
    dummy.customerId = 0;
    dummy.createdBy = this.userService.currentUser.accountId;
    dummy.creationDate = new Date();
    dummy.modifiedBy = this.userService.currentUser.accountId;
    dummy.modifiedDate = new Date();
    dummy.dueDate = null;
    dummy.lexoRank = LexoRank.max().toString();
    dummy.flagged = false;
    dummy.flagColor = FlagColor.None;
    return dummy;
  }

  getDummyJobDto(): JobDto {
    return {
      job: this.getDummyJob(),
      assignee: {
        accountId: '',
        username: '',
        groupId: 1,
        avatarUrl: '',
      },
      customer: {
        id: 1,
        createdBy: '',
        creationDate: undefined,
        modifiedBy: '',
        modifiedDate: undefined,
        version: 1,
        deleted: false,
        isLead: true,
        contactName: '',
        companyName: '',
        email: '',
        phoneNumber: '',
        address1: '',
        address2: '',
        city: '',
        zip: '',
        state: '',
        county: '',
        email2: '',
        email3: '',
        dba1: '',
        entityType: '',
        ein: '',
        notes: '',
        phoneNumberType: '',
        businessPhoneNumber: '',
        altPhoneNumber: '',
        altPhoneNumberType: ''
      },
      parent: undefined,
      modifier: {
        accountId: '',
        username: '',
        groupId: 1,
        avatarUrl: '',
      },
      creator: {
        accountId: '',
        username: '',
        groupId: 1,
        avatarUrl: '',
      },
      level: 1,
      hasChildren: false,
      progress: 0
    };
  }

  async getJobHistory(jobId: number, force: boolean = false): Promise<GenericVersion[]> {
    if (jobId <= 0) {
      return undefined;
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, 'Failed to fetch job audit!');
    }
    return await this.httpService.applicationApiClient.getWithParams<GenericVersion[]>(`Job/History/${jobId}`, {force});
  }

  async getJobVersion(jobId: number, version: number): Promise<JobDto> {
    if (jobId <= 0 || version <= 0) {
      return undefined;
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, 'Failed to fetch job audit!');
    }
    return await this.httpService.applicationApiClient.getWithParams<JobDto>(`Job/History/${jobId}/${version}`, null);
  }

  async getJobVersionLabels(jobId: number, version: number, force: boolean = false): Promise<string[]> {
    if (jobId <= 0 || version <= 0) {
      return undefined;
    }
    try {
      return await this.httpService.applicationApiClient.getWithParams<string[]>(`Job/History/${jobId}/${version}/Labels`, null);
    } catch (e) {
      return ['INITIAL'];
    }
  }

  async addJob(job: Job) {
    const request = new AddJobRequest();

    this.pleaseWaitService.addToQueue();
    request.job = job;
    if (job.assigneeId && job.assigneeId.length > 0) {
      request.hasAssignee = true;
    }
    request.hasCustomer = job.customerId !== undefined && job.customerId > 0;
    request.hasDueDate = job.dueDate !== undefined;

    if (job.flagColor !== -1) {
      request.job.flagged = true;
    } else {
      request.job.flagged = false;
    }

    try {
      await this.httpService.applicationApiClient.post('Job', request).then(() => {
        this.appService.showSuccessMessage('Job Added', {windowId: 'main'});
        this.pleaseWaitService.removeFromQueue();
        this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
          message: 'Add job', entity: job
        });
        return true;
      });
    } catch (ex) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, {
        message: 'Add job', entity: ex
      });
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async editJob(jobDto: JobDto, emailNotifications: EmailNotificationDto, force: boolean = false): Promise<JobDto | undefined> {
    jobDto = this.trimJobDtoNames(jobDto);
    const request = new EditJobRequest();
    const job = jobDto.job;
    if (!job.parentId && (job.customerId <= 0 || job.customerId === undefined)) {
      this.appService.showErrorMessage('Please set a contact.');
      return undefined;
    }

    this.pleaseWaitService.addToQueue();
    request.job = job;
    if (job.assigneeId && job.assigneeId.length > 0) {
      request.hasAssignee = true;
    }
    request.hasCustomer = job.customerId !== undefined && job.customerId > 0;
    request.hasDueDate = job.dueDate !== undefined;
    request.emailNotifications = emailNotifications;

    request.job.flagged = job.flagColor !== -1;

    request.force = force;

    try {
      const responsePromise = this.httpService.applicationApiClient.put<JobDto>('Job', job.id, request);
      responsePromise.then(() => {
        this.appService.showSuccessMessage('Job Edited');
        this.pleaseWaitService.removeFromQueue();
      });
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
        message: 'Edit job', entity: job
      });
      const response = await responsePromise;
      return response;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, {
        message: 'Edit job', entity: ex
      });
      if (ex instanceof ConflictException) {
        this.onConflict.emit({my: jobDto, db: ex.conflict});
      } else {
        throw ex;
      }
    }
  }

  async revertJob(jobDto: JobDto, version: number): Promise<boolean> {
    const jobId = jobDto.job.id;
    this.pleaseWaitService.addToQueue();

    try {
      const responsePromise = await this.httpService.applicationApiClient.get(`Job/History/${jobId}/${version}/Revert`).then(() => {
        this.appService.showSuccessMessage('Job Reverted');
        this.pleaseWaitService.removeFromQueue();
      });
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
        message: 'Revert job', entity: responsePromise
      });
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, {
        message: 'Edit job', entity: ex
      });
      if (ex instanceof ConflictException) {
        this.onConflict.emit({my: jobDto, db: ex.conflict});
      } else {
        throw ex;
      }
      return false;
    }
  }

  async deleteJob(id: number): Promise<boolean> {
    this.pleaseWaitService.addToQueue();
    try {
      await this.httpService.applicationApiClient.delete(`Job`, id).then(() => {
        this.appService.showSuccessMessage('Job Deleted');
        this.pleaseWaitService.removeFromQueue();
        this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
          message: 'Delete job', entity: id
        });
        return true;
      });
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, {
        message: 'Delete job', entity: ex
      });
      return false;
    }
  }


  async changeLexoRank(jobId: number, previousLexoRank: string, nextLexoRank: string) {
    const request = new ChangeLexoRankRequest();
    request.previous = previousLexoRank;
    request.next = nextLexoRank;

    this.pleaseWaitService.addToQueue();
    try {
      await this.httpService.applicationApiClient.patchWithCustomURL(`Job/${jobId}/LexoRank`, request).then(() => {
        this.pleaseWaitService.removeFromQueue();
        this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
          message: 'Change lexoRank job', entity: jobId
        });
        return true;
      });
    } catch (ex) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, {
        message: 'Change lexoRank job', entity: ex
      });
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async changeFlaggedStatus(jobId: number, flagged: boolean, flagColor: FlagColor) {
    const request = new ChangeFlaggedStatusRequest();
    request.flagged = flagged;
    request.flagColor = flagColor;

    this.pleaseWaitService.addToQueue();
    try {
      await this.httpService.applicationApiClient.patchWithCustomURL(`Job/${jobId}/Flag`, request).then(() => {
        this.pleaseWaitService.removeFromQueue();
        this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
          message: 'Change flagged status', entity: jobId
        });
        return true;
      });
    } catch (ex) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.JobService, {
        message: 'Change flagged status', entity: ex
      });
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async searchJobs(jobQuery: QueryDTO): Promise<JobDto[]> {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.JobService, {
      message: 'Search jobs by queryDto', entity: jobQuery
    });
    const jobsDto = await this.httpService.applicationApiClient.getWithParams<JobDto[]>('Job/Search', {filter: JSON.stringify(jobQuery)});
    if (jobsDto) {
      jobsDto.forEach(job => {
        job = this.trimJobDtoNames(job);
      });
      return jobsDto;
    }
  }

  async search(queryText, count: number, offset: number, customerId) {
    let dto;
    if (customerId){
      dto = QueryDTO.create(offset, count, [QueryUnion.create(
          QueryFilter.group([
            QueryFilter.singleValue('title', 'contains', queryText),
            QueryFilter.singleValue('customerId', 'eq', customerId.toString()),
            QueryFilter.singleValue('deleted', 'eq', 'false')
          ], 'and'))]
        , [QuerySort.create('title', 'asc')]);
    } else {
      dto = QueryDTO.create(offset, count, [QueryUnion.create(
          QueryFilter.group([
            QueryFilter.singleValue('title', 'contains', queryText),
            QueryFilter.singleValue('deleted', 'eq', 'false')
          ], 'and'))]
        , [QuerySort.create('title', 'asc')]);
    }
    const response = await this.httpService.applicationApiClient.getWithParams<JobDto[]>('Job/Search', {filter: JSON.stringify(dto)});
    return response;
  }

  trimJobDtoNames(jobDto: JobDto): JobDto {
    if (jobDto) {
      if (jobDto.job && jobDto.job.title) {
        jobDto.job.title = jobDto.job.title.trim();
      }
      if (jobDto.job && jobDto.job.description) {
        jobDto.job.description = jobDto.job.description.trim();
      }
      if (jobDto.job && jobDto.job.createdBy) {
        jobDto.job.createdBy = jobDto.job.createdBy.trim();
      }
      if (jobDto.job && jobDto.job.modifiedBy) {
        jobDto.job.modifiedBy = jobDto.job.modifiedBy.trim();
      }
      if (jobDto.customer && jobDto.customer.contactName) {
        jobDto.customer.contactName = jobDto.customer.contactName.trim();
      }
      if (jobDto.customer && jobDto.customer.companyName) {
        jobDto.customer.companyName = jobDto.customer.companyName.trim();
      }
      if (jobDto.customer && jobDto.customer.email) {
        jobDto.customer.email = jobDto.customer.email.trim();
      }
      if (jobDto.customer && jobDto.customer.modifiedBy) {
        jobDto.customer.modifiedBy = jobDto.customer.modifiedBy.trim();
      }
      if (jobDto.customer && jobDto.customer.createdBy) {
        jobDto.customer.createdBy = jobDto.customer.createdBy.trim();
      }
      if (jobDto.modifier && jobDto.modifier.username) {
        jobDto.modifier.username = jobDto.modifier.username.trim();
      }
      if (jobDto.assignee && jobDto.assignee.username) {
        jobDto.assignee.username = jobDto.assignee.username.trim();
      }
      if (jobDto.creator && jobDto.creator.username) {
        jobDto.creator.username = jobDto.creator.username.trim();
      }
      if (jobDto.parent) {
        jobDto.parent = this.trimJobDtoNames(jobDto.parent);
      }
    }
    return jobDto;
  }

  async reparentJob(draggedJobId: number, droppedJobId: number) {
    if (droppedJobId === 0) {
      droppedJobId = null;
    }
    return await this.httpService.applicationApiClient.patchWithCustomURL(`Job/${draggedJobId}/Reparent`, {id: draggedJobId, parentId: droppedJobId});
  }
}
